<template>
  <div class="Module__Features">
    <div v-if="mode === 'carousel'" class="type-carousel">
      <div ref="swiper" class="Component_Swiper swiper">
        <div class="swiper-wrapper" :style="getSwiperStyle()">
          <div
            v-for="(feat, idx) in displayFeatures"
            :key="feat.id"
            :class="getCardClass()"
            @mouseleave.self="handleLeave"
          >
            <Item
              :feat="feat"
              :idx="idx"
              :current="currentHover"
              :data-idx="idx"
              :loading="loading"
              :count="count"
              @mouseenter.self="handEnter"
              @enter="handEnter"
              @load="loadPayload"
              @click="handleSlideClick(feat)"
            />
          </div>
        </div>
      </div>
      <div
        ref="prevEl"
        :class="['x-button-prev', { disable: currentPage <= 0 }]"
        @click="handlePrev"
      >
        <iconpark-icon name="circle-arrow" size="24"></iconpark-icon>
      </div>
      <div
        ref="nextEl"
        :class="['x-button-next', { disable: currentPage >= totalPage - 1 }]"
        @click="handleNext"
      >
        <iconpark-icon name="circle-arrow" size="24"></iconpark-icon>
      </div>
    </div>
    <div v-else-if="mode === 'grid'" class="type-grid">
      <a-button
        v-if="(features || []).length > 8"
        type="link"
        class="more-btn flex justify-center items-center cursor-pointer"
        @click="more = !more"
      >
        {{ !more ? $t('zhan-kai-quan-bu') : $t('shou-qi-quan-bu') }}
        <iconpark-icon :name="!more ? 'arrowright' : 'arrowdown'" size="16"></iconpark-icon>
      </a-button>
      <div
        v-for="(feat, idx) in displayFeatures"
        :key="feat.id"
        :class="getCardClass()"
        @mouseenter.self="handleLeave"
      >
        <Item
          :feat="feat"
          :idx="idx"
          :current="currentHover"
          :data-idx="idx"
          :loading="loading"
          :count="count"
          @mouseenter.self="handEnter"
          @enter="handEnter"
          @load="loadPayload"
          @click="handleSlideClick(feat)"
        />
      </div>
    </div>
    <VideoModal v-model:open="open" :data="activeHighlight" />
  </div>
</template>

<script setup lang="ts">
import type { IProductHighlight } from '~/api/interface/publicize';
import { getProductHighlightsList } from '~/api/module/publicize';
import Item from './item.vue';
import VideoModal from './modal.vue';

const loading = ref(false);

const props = defineProps({
  // 展示模式
  mode: {
    type: String,
    default: 'carousel',
    enum: ['carousel', 'grid'],
  },
  // 每行展示个数
  count: {
    type: Number,
    default: 4,
  },
});

const { data: features } = useAsyncData(async () => {
  const res = await getProductHighlightsList();
  return res.data;
});

const displayFeatures = computed(() => {
  if (props.mode === 'grid') {
    return (more.value ? features.value : (features.value || []).slice(0, 8)) || [];
  }
  return features.value || [];
});

const open = ref(false);
const activeHighlight = ref<IProductHighlight>();

const prevEl = ref(null);
const nextEl = ref(null);
const currentPage = ref(0);

const more = ref(false);

const currentHover = ref<number | undefined>();

const totalPage = computed(() => Math.ceil((features.value || []).length / props.count));

const loadPayload = () => {
  loading.value = true;
};

const handlePrev = () => {
  if (currentPage.value <= 0) {
    return;
  }
  currentPage.value = Math.max(0, currentPage.value - 1);
};
const handleNext = () => {
  if (currentPage.value >= totalPage.value - 1) {
    return;
  }
  currentPage.value = Math.min(totalPage.value, currentPage.value + 1);
};

const getSwiperStyle = () => {
  if (currentPage.value === totalPage.value - 1) {
    const mod = (features.value || []).length % props.count;
    const percent = (mod / props.count) * 100;
    const offset = mod === 0 ? currentPage.value * 100 : (currentPage.value - 1) * 100 + percent;
    return {
      transform: `translateX(-${offset}%)`,
    };
  }
  return {
    transform: `translateX(-${currentPage.value * 100}%)`,
  };
};

const getCardClass = () => {
  return {
    'swiper-slide': true,
    feature: true,
  };
};

const sliderWidth = computed(() => `${(1 * 100) / props.count}%`);

const handEnter = (e: MouseEvent) => {
  const idx = (e.target as HTMLDivElement).dataset.idx;
  currentHover.value = idx ? +idx : undefined;
};

const handleLeave = () => {
  currentHover.value = undefined;
  loading.value = false;
};

const handleSlideClick = (feat: IProductHighlight) => {
  trackEvent(TRACK_EVENT.CLICK_PRODUCT_HIGHLIGHTS_CARD, {
    title: feat.title,
  });
  open.value = true;
  activeHighlight.value = feat;
};

defineExpose({
  features,
});
</script>

<style lang="scss" scoped>
.v-enter-active {
  transition: opacity 0.5s ease;
}

.v-enter-from {
  opacity: 0;
}

.Module__Features {
  position: relative;
  z-index: 12;
  width: 100%;
  box-sizing: border-box;

  .type-carousel {
    .Component_Swiper {
      width: calc(100% + 16px);
      display: flex;
      align-items: center;
      margin-left: -8px;

      :deep(.swiper-wrapper) {
        height: 168px;
        position: relative;
        overflow: visible;
        z-index: 22;
      }

      .feature {
        display: flex;
        position: relative;
        width: v-bind('sliderWidth');
        padding: 0 8px;
        transition: width 0.6s ease-in-out;
      }
    }
    .x-button-prev,
    .x-button-next {
      position: absolute;
      right: 0;
      top: -40px;
      cursor: pointer;
      height: 24px;
      width: 24px;
      background-color: var(--back-bg);
      color: var(--text-1);
      &.disable {
        cursor: not-allowed;
        opacity: 0.4;
      }
    }

    .x-button-prev {
      transform: rotate(180deg);
      right: 30px;
    }

    .swiper {
      margin-left: auto;
      margin-right: auto;
      position: relative;
      overflow: hidden;
      list-style: none;
      z-index: 1;
      display: block;
    }

    .swiper-wrapper {
      transform: translate3d(0, 0, 0); // 加速
      display: flex;
      transition: all 0.6s ease-in-out;
      position: relative;
      width: 100%;
      height: 100%;
      z-index: 1;
      display: flex;
      box-sizing: content-box;
    }

    .swiper-slide {
      flex-shrink: 0;
      width: 100%;
      height: 100%;
      position: relative;
      z-index: 11;
      transition-property: transform;
      display: block;
    }
  }

  .type-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(288px, 1fr));
    gap: 16px;
    position: relative;
    .more-btn {
      position: absolute;
      right: 0;
      top: -40px;
      font-size: 14px;
      line-height: 1;
      color: var(--text-1);
      opacity: 0.6;
      &:hover {
        opacity: 0.9;
      }
    }
    .feature {
      width: 288px;
      height: 162px;
    }
  }
}
</style>
